import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import Header from '../components/layout/Header';
import NavigationButton from '../components/layout/Navigation';
import ShareButton from '../components/campaigns/ShareButton';
import { getCourseBySlug, getMyEnrolledCourses, createEnrollment, toggleLikeCourse } from '../services/ecourseApi';
import { formatCurrency } from '../utils/formatters';
import { getMediaUrl } from '../utils/mediaUtils';
import UserProfileModal from '../components/modals/UserProfileModal';
import '../styles/Body.css';

const formatIDR = (amount) => {
  if (amount <= 0) return 'GRATIS';
  return 'Rp ' + formatCurrency(amount);
};

const EcourseCourseDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('description');
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '', image: null });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [shareMessage, setShareMessage] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [liking, setLiking] = useState(false);

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/api/courses/share/${slug}`;
    const title = `${course?.title} - Barakah Economy`;

    if (navigator.share) {
      navigator.share({
        title: title,
        url: shareUrl
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(shareUrl)
        .then(() => {
          setShareMessage('Link disalin!');
          setTimeout(() => setShareMessage(''), 3000);
        })
        .catch(err => {
          console.error('Gagal menyalin link:', err);
        });
    }
  };

  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/reviews/products/0/reviews/?course_id=${course?.id}`);
      setReviews(res.data);
    } catch (err) {
      console.error("Error fetching reviews:", err);
    }
  }, [course?.id]);

  useEffect(() => {
    if (course) fetchReviews();
  }, [course, fetchReviews]);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('access_token');
    if (!token) return;

    if (reviewForm.image && reviewForm.image.size > 5 * 1024 * 1024) {
      alert("Ukuran gambar maksimal 5MB");
      return;
    }

    setSubmittingReview(true);
    const formData = new FormData();
    formData.append('course_id', course.id);
    formData.append('rating', reviewForm.rating);
    formData.append('comment', reviewForm.comment);
    if (reviewForm.image) {
      formData.append('image', reviewForm.image);
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/reviews/products/0/reviews/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      alert("Review berhasil dikirim!");
      setReviewForm({ rating: 5, comment: '', image: null });
      fetchReviews();
    } catch (err) {
      alert("Gagal mengirim review. Pastikan Anda sudah memberikan review sebelumnya atau coba lagi.");
    } finally {
      setSubmittingReview(false);
    }
  };

  useEffect(() => {
    const fetchCourseDetails = async () => {
      try {
        const res = await getCourseBySlug(slug);
        setCourse(res.data);
        setIsLiked(res.data.is_liked);
        setLikesCount(res.data.likes_count);
      } catch (err) {
        setError('Failed to load course details');
      } finally {
        setLoading(false);
      }
    };
    fetchCourseDetails();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  useEffect(() => {
    if (!course) return;
    const fetchRecommendations = async () => {
      try {
        setLoadingRecommendations(true);
        const res = await axios.get(`${baseUrl}/api/courses/`);
        const all = res.data || [];
        const currentCourseId = String(course.id);
        const currentCourseSlug = String(course.slug || '');
        const currentCategory = String(course.category || '').toLowerCase().trim();

        const others = all.filter(c => String(c.id) !== currentCourseId && String(c.slug || '') !== currentCourseSlug);

        const sameCategory = others.filter(c => {
          if (!currentCategory || !c.category) return false;
          return String(c.category).toLowerCase().trim() === currentCategory;
        });

        const diffCategory = others.filter(c => {
          if (!currentCategory || !c.category) return true;
          return String(c.category).toLowerCase().trim() !== currentCategory;
        });

        const shuffle = (arr) => {
          const copy = [...arr];
          for (let i = copy.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [copy[i], copy[j]] = [copy[j], copy[i]];
          }
          return copy;
        };

        const result = [...shuffle(sameCategory), ...shuffle(diffCategory)].slice(0, 8);
        setRecommendedCourses(result);
      } catch (err) {
        console.error('Error fetching recommended courses:', err);
      } finally {
        setLoadingRecommendations(false);
      }
    };
    fetchRecommendations();
  }, [course?.id, course?.slug, course?.category, baseUrl]);

  useEffect(() => {
    const checkEnrollment = async () => {
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user) return;

      try {
        const res = await getMyEnrolledCourses();
        // Check if user is enrolled in this course and payment_status is 'verified' or 'paid'
        const enrolled = res.data.some(
          enroll => (enroll.course === course.id || enroll.course === course.slug) && ['paid', 'verified'].includes(enroll.payment_status)
        );
        setIsEnrolled(enrolled);
      } catch {
        setIsEnrolled(false);
      }
    };
    if (course) checkEnrollment();
  }, [course]);

  const handleEnroll = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      alert('Anda harus login terlebih dahulu untuk mendaftar E-Course.');
      navigate('/login');
      return;
    }
    navigate(`/ikut-kelas/${course.slug || course.id}`);
  };

  const handleToggleLike = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user) {
      alert('Anda harus login terlebih dahulu untuk memberikan like.');
      return;
    }

    setLiking(true);
    // Optimistic UI
    const prevIsLiked = isLiked;
    const prevLikesCount = likesCount;
    setIsLiked(!prevIsLiked);
    setLikesCount(prevLikesCount + (prevIsLiked ? -1 : 1));

    try {
      const res = await toggleLikeCourse(course.slug || course.id);
      setIsLiked(res.data.liked);
      setLikesCount(res.data.likes_count);
    } catch (err) {
      console.error('Error toggling like:', err);
      // Rollback
      setIsLiked(prevIsLiked);
      setLikesCount(prevLikesCount);
    } finally {
      setLiking(false);
    }
  };

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (error) return <div className="text-center py-8 text-red-500">{error}</div>;
  if (!course) return <div className="text-center py-8 text-red-500">Kelas tidak ditemukan.</div>;

  const convertRelativeUrlsToAbsolute = (htmlContent, baseUrl) => {
    // Ensure baseUrl does not have a trailing slash
    if (baseUrl.endsWith('/')) {
      baseUrl = baseUrl.slice(0, -1);
    }
    // Convert relative image URLs to absolute URLs
    return htmlContent.replace(/<img[^>]+src="(\/[^"]+)"[^>]*>/g, (match, src) => {
      return match.replace(src, `${baseUrl}${src}`);
    });
  };

  return (
    <div className="body">
      <Helmet>
        <title>{course.title} - Kelas & E-Course Online | Barakah Academy</title>
        <meta name="description" content={course.description?.replace(/<[^>]+>/g, '').slice(0, 160) || `Ikuti kelas online ${course.title} bersama para pakar di Barakah Academy.`} />
        <meta property="og:title" content={`${course.title} - Barakah Academy`} />
        <meta property="og:description" content={course.description?.replace(/<[^>]+>/g, '').slice(0, 160) || `Ikuti kelas online ${course.title} bersama para pakar di Barakah Academy.`} />
        <meta property="og:image" content={getMediaUrl(course.thumbnail)} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <link rel="canonical" href={window.location.href} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            "name": course.title,
            "description": course.description?.replace(/<[^>]+>/g, '').slice(0, 200),
            "provider": {
              "@type": "Organization",
              "name": "Barakah Academy",
              "sameAs": "https://barakah.cloud/academy"
            },
            "offers": {
              "@type": "Offer",
              "price": String(course.price || 0),
              "priceCurrency": "IDR",
              "category": (!course.price || Number(course.price) <= 0) ? "Free" : "Paid",
              "availability": "https://schema.org/InStock",
              "url": window.location.href
            }
          })}
        </script>
      </Helmet>

      <Header />
      {/* Course Details */}
      <div className="px-4 pt-4 max-w-6xl mx-auto">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-4 overflow-x-auto whitespace-nowrap">
          <Link to="/" className="hover:text-emerald-700 transition">Beranda</Link>
          <span className="text-gray-300">/</span>
          <Link to="/academy/ecourse" className="hover:text-emerald-700 transition">Academy E-Course</Link>
          <span className="text-gray-300">/</span>
          <span className="text-gray-800 font-semibold truncate max-w-[200px] md:max-w-md">{course.title}</span>
        </div>

        <div className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100 flex flex-col md:flex-row">
          <div className="md:w-1/2 p-4 md:p-6 bg-gray-50 flex items-center justify-center">
            <div className="relative w-full rounded-2xl overflow-hidden shadow-sm border border-gray-100 bg-white">
              {course.category && (
                <div className="absolute top-3 left-3 z-10 bg-emerald-600/95 backdrop-blur-xs text-white text-[10px] font-black px-3 py-1 rounded-xl shadow uppercase tracking-wider flex items-center gap-1">
                  <span className="material-icons text-xs">school</span>
                  {course.category}
                </div>
              )}
              {(!course.price || Number(course.price) <= 0) && (
                <div className="absolute top-3 right-14 z-10 bg-emerald-700 text-white text-[9px] font-black px-2.5 py-1 rounded-xl shadow uppercase tracking-wider">
                  GRATIS
                </div>
              )}
              <img
                src={getMediaUrl(course.thumbnail) || '/placeholder-image.jpg'}
                alt={course.title}
                className="w-full h-64 md:h-80 object-cover"
                onError={(e) => {
                  e.target.src = '/placeholder-image.jpg';
                }}
              />
              <div className="absolute top-3 right-3 z-10">
                <ShareButton slug={course.slug} title={course.title} type="course" variant="card-icon" />
              </div>
            </div>
          </div>

          <div className="md:w-1/2 p-6 md:p-8 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-start gap-4 mb-2">
                <h1 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight leading-snug">
                  {course.title}
                </h1>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 mb-4 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700">
                    <span className="material-icons text-[16px]">school</span>
                  </div>
                  <div className="text-xs">
                    <span className="text-gray-400 block text-[10px]">Instruktur Kelas</span>
                    <span 
                      className="text-emerald-700 font-bold hover:underline cursor-pointer"
                      onClick={() => {
                        setSelectedUserId(course.instructor);
                        setIsProfileModalOpen(true);
                      }}
                    >
                      @{course.instructor_name}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <div className="flex items-center gap-1" title="Dilihat">
                    <span className="material-icons text-sm text-emerald-600">visibility</span>
                    <span className="font-semibold text-gray-600">{course.view_count || 0}</span>
                  </div>
                  <button 
                    onClick={handleToggleLike}
                    disabled={liking}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-xl transition-all active:scale-90 ${
                      isLiked ? 'bg-red-50 text-red-600 border border-red-100 font-bold' : 'bg-gray-50 text-gray-500 border border-gray-200'
                    }`}
                  >
                    <span className={`material-icons text-sm ${isLiked ? 'text-red-500' : 'text-gray-400'}`}>
                      {isLiked ? 'favorite' : 'favorite_border'}
                    </span>
                    <span>{likesCount}</span>
                  </button>
                </div>
              </div>

              {/* Price Box */}
              <div className="bg-emerald-50/70 border border-emerald-100 rounded-2xl p-4 mb-6">
                <div className="text-[10px] font-black text-emerald-800 uppercase tracking-wider mb-0.5">
                  Biaya Investasi Kelas
                </div>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl md:text-3xl font-black text-emerald-700">
                    {formatIDR(course.price)}
                  </p>
                  <span className="text-xs text-gray-400 font-medium">/ akses penuh seumur hidup</span>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100">
              {!isEnrolled ? (
                <button
                  onClick={handleEnroll}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3.5 rounded-2xl font-black text-sm flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 transition-all transform active:scale-95"
                >
                  <span className="material-icons text-base">school</span>
                  IKUTI KELAS SEKARANG • {formatIDR(course.price)}
                </button>
              ) : (
                <button
                  onClick={() => navigate(`/kelas/buka/${course.slug || course.id}`)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3.5 rounded-2xl font-black text-sm flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 transition-all transform active:scale-95"
                >
                  <span className="material-icons text-base">play_circle</span>
                  MULAI BELAJAR
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation & Content Container */}
      <div className="max-w-6xl mx-auto mb-20 px-4">
        {/* Tab Navigation */}
        <div className="mt-6">
          <div className="flex justify-around bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden p-1">
            <button
              className={`py-3 px-4 text-xs md:text-sm font-bold rounded-xl transition ${
                activeTab === 'description' ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-500 hover:text-emerald-700'
              }`}
              onClick={() => setActiveTab('description')}
            >
              Keterangan
            </button>
            <button
              className={`py-3 px-4 text-xs md:text-sm font-bold rounded-xl transition ${
                activeTab === 'students' ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-500 hover:text-emerald-700'
              }`}
              onClick={() => setActiveTab('students')}
            >
              Peserta ({course.student_count || 0})
            </button>
            <button
              className={`py-3 px-4 text-xs md:text-sm font-bold rounded-xl transition ${
                activeTab === 'materials' ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-500 hover:text-emerald-700'
              }`}
              onClick={() => setActiveTab('materials')}
            >
              Materi ({course.material_count || 0})
            </button>
            <button
              className={`py-3 px-4 text-xs md:text-sm font-bold rounded-xl transition ${
                activeTab === 'reviews' ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-500 hover:text-emerald-700'
              }`}
              onClick={() => setActiveTab('reviews')}
            >
              Review
            </button>
          </div>

          {/* Tab Content */}
          <div className="mt-4">
            {activeTab === 'description' && (
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 min-h-[200px]">
                {course.description ? (
                  <>
                    <div
                      className="prose max-w-none text-gray-700 text-sm leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: showFullDescription
                          ? convertRelativeUrlsToAbsolute(course.description, baseUrl)
                          : convertRelativeUrlsToAbsolute(course.description, baseUrl).substring(0, 500) + '...',
                      }}
                    />
                    {course.description.length > 500 && (
                      <button
                        onClick={toggleDescription}
                        className="text-green-600 mt-4 text-xs font-bold uppercase tracking-wider bg-green-50 px-4 py-2 rounded-full hover:bg-green-100 transition"
                      >
                        {showFullDescription ? 'Tampilkan Lebih Sedikit' : 'Tampilkan Selengkapnya'}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-gray-400 italic">Tidak ada deskripsi.</p>
                )}
              </div>
            )}

            {activeTab === 'students' && (
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {course.students && course.students.length > 0 ? (
                    course.students.map((student, idx) => (
                      <div key={idx} className="bg-gray-50 p-3 rounded-xl flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold text-xs">
                          {student.full_name ? student.full_name.charAt(0).toUpperCase() : (student.username ? student.username.charAt(0).toUpperCase() : 'U')}
                        </div>
                        <span className="text-gray-700 font-bold text-xs truncate">{student.full_name || student.username}</span>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full py-10 text-center">
                      <span className="material-symbols-outlined text-gray-200 text-4xl block mb-2">person_off</span>
                      <p className="text-gray-400 text-xs italic">Belum ada peserta terdaftar.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'materials' && (
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 min-h-[200px]">
                <h3 className="text-sm font-bold mb-4">Kurikulum Kelas</h3>
                <div className="space-y-3">
                  {course.materials && course.materials.length > 0 ? (
                    course.materials.map((mat, idx) => (
                      <div key={idx} className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 border border-gray-100">
                        <div className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold text-xs ring-4 ring-green-50">
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-bold text-gray-800 truncate">{mat.title}</h4>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                              <span className="material-icons text-xs">videocam</span> Video
                            </span>
                            {mat.pdf_file && (
                              <span className="text-[10px] text-blue-500 flex items-center gap-0.5 font-bold">
                                <span className="material-icons text-xs">description</span> Modul PDF
                              </span>
                            )}
                          </div>
                        </div>
                        {!isEnrolled ? (
                          <span className="material-icons text-gray-300 text-sm">lock</span>
                        ) : (
                          <span className="material-icons text-green-600 text-sm">play_circle_filled</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-400 italic text-center py-10">Materi belum tersedia.</p>
                  )}
                </div>
                {!isEnrolled && (
                  <div className="mt-8 p-4 bg-orange-50 border border-orange-100 rounded-2xl text-center">
                    <p className="text-xs text-orange-700 font-medium">Beli kelas untuk membuka akses ke semua materi dan video pembelajaran.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-6">
                {/* Review Form (Only for enrolled users) */}
                {isEnrolled && (
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-green-100">
                    <h3 className="text-sm font-bold mb-4">Berikan Review Anda</h3>
                    <form onSubmit={handleReviewSubmit} className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Rating</label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map(star => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                              className={`material-icons ${star <= reviewForm.rating ? 'text-orange-400' : 'text-gray-300'}`}
                            >
                              star
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Komentar</label>
                        <textarea
                          required
                          rows="3"
                          value={reviewForm.comment}
                          onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-50 border-none rounded-xl text-xs"
                          placeholder="Ceritakan pengalaman Anda mengikuti kelas ini..."
                        ></textarea>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Upload Gambar (Optional, Max 5MB)</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setReviewForm({ ...reviewForm, image: e.target.files[0] })}
                          className="text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-[10px] file:font-bold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submittingReview}
                        className="w-full py-3 bg-green-700 text-white rounded-xl text-xs font-bold disabled:bg-gray-400"
                      >
                        {submittingReview ? 'Mengirim...' : 'Kirim Review'}
                      </button>
                    </form>
                  </div>
                )}

                {/* Reviews List */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                  <h3 className="text-sm font-bold mb-6">Review Peserta</h3>
                  {reviews.length === 0 ? (
                    <p className="text-center text-gray-400 text-xs italic py-10">Belum ada review untuk kelas ini.</p>
                  ) : (
                    <div className="space-y-6">
                      {reviews.map(review => (
                        <div key={review.id} className="border-b border-gray-50 pb-6 last:border-0 last:pb-0">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center font-bold text-xs text-gray-500">
                                {review.username?.[0]?.toUpperCase() || 'U'}
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-800">{review.username}</p>
                                <p className="text-[10px] text-gray-400">{new Date(review.created_at).toLocaleDateString('id-ID')}</p>
                              </div>
                            </div>
                            <div className="flex text-orange-400">
                              {[...Array(5)].map((_, i) => (
                                <span key={i} className="material-icons text-[14px]">{i < review.rating ? 'star' : 'star_border'}</span>
                              ))}
                            </div>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed mb-3">{review.comment}</p>
                          {review.image && (
                            <img
                              src={getMediaUrl(review.image)}
                              alt="Review"
                              className="w-32 h-32 object-cover rounded-lg border border-gray-100 shadow-sm"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Rekomendasi Kelas Lainnya */}
        {recommendedCourses.length > 0 && (
          <div className="mt-14 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-6 bg-emerald-600 rounded-full"></span>
                <h2 className="text-base md:text-xl font-black text-gray-900 tracking-tight">
                  Rekomendasi Kelas Lainnya
                </h2>
              </div>
              <Link 
                to="/academy/ecourse" 
                className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-0.5"
              >
                <span>Lihat Semua Kelas</span>
                <span className="material-icons text-sm">chevron_right</span>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
              {recommendedCourses.map((rec) => {
                const recSlug = rec.slug || rec.id;
                const isFree = !rec.price || Number(rec.price) <= 0;

                return (
                  <div 
                    key={rec.id} 
                    className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-gray-100 flex flex-col justify-between group"
                  >
                    <div className="relative">
                      {rec.category && (
                        <div className="absolute top-2 left-2 z-10 bg-emerald-600/95 backdrop-blur-xs text-white text-[9px] font-black px-2.5 py-1 rounded-xl shadow uppercase tracking-wider flex items-center gap-1">
                          <span className="material-icons text-[11px]">school</span>
                          {rec.category}
                        </div>
                      )}
                      {isFree && (
                        <div className="absolute top-2 right-10 z-10 bg-emerald-700 text-white text-[9px] font-black px-2 py-0.5 rounded-lg shadow uppercase">
                          GRATIS
                        </div>
                      )}
                      <Link to={`/kelas/${recSlug}`}>
                        <img
                          src={getMediaUrl(rec.thumbnail) || '/placeholder-image.jpg'}
                          alt={rec.title}
                          className="w-full h-36 md:h-44 object-cover group-hover:scale-105 transition-transform duration-300 bg-gray-50"
                          onError={(e) => { e.target.src = '/placeholder-image.jpg'; }}
                        />
                      </Link>
                      <div className="absolute top-2 right-2 z-10">
                        <ShareButton
                          slug={recSlug}
                          title={rec.title}
                          type="course"
                          price={rec.price}
                          description={rec.description}
                          variant="card-icon"
                        />
                      </div>
                    </div>

                    <div className="p-3.5 flex flex-col flex-1 justify-between">
                      <div>
                        <div className="flex items-center gap-1 text-gray-500 text-[10px] font-medium mb-1">
                          <span className="material-icons text-[12px] text-emerald-600 shrink-0">school</span>
                          <span className="truncate max-w-[130px]" title={rec.instructor_name}>
                            @{rec.instructor_name}
                          </span>
                        </div>

                        <h3 className="text-xs md:text-sm font-semibold mb-1.5 line-clamp-2 min-h-[36px] text-gray-800 hover:text-emerald-700 transition-colors">
                          <Link to={`/kelas/${recSlug}`}>
                            {rec.title}
                          </Link>
                        </h3>

                        <div className="mb-2">
                          <p className="text-emerald-700 font-bold text-sm">
                            {formatIDR(rec.price)}
                          </p>
                          <div className="flex items-center justify-between text-[10px] mt-0.5 text-gray-400">
                            <span>{rec.material_count ? `${rec.material_count} materi` : 'Akses Penuh'}</span>
                            {rec.student_count > 0 && (
                              <span className="font-bold text-emerald-700 bg-emerald-50 px-1 rounded">
                                {rec.student_count} peserta
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <Link
                        to={`/kelas/${recSlug}`}
                        className="w-full bg-emerald-50 hover:bg-emerald-100 text-emerald-800 py-2 rounded-xl font-bold flex items-center justify-center gap-1 transition-all text-[11px] border border-emerald-200 mt-2"
                      >
                        <span>Lihat Kelas</span>
                        <span className="material-icons text-xs">arrow_forward</span>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>


      <NavigationButton />
      <UserProfileModal 
        userId={selectedUserId} 
        isOpen={isProfileModalOpen} 
        onClose={() => setIsProfileModalOpen(false)} 
      />

      {/* Fixed share button for mobile */}
      <div className="md:hidden fixed bottom-24 right-4 z-50">
        <button
          onClick={handleShare}
          className="bg-white border border-gray-200 text-gray-600 w-12 h-12 rounded-full shadow-lg flex items-center justify-center relative active:scale-95 transition-transform"
        >
          <span className="material-icons">share</span>
          {shareMessage && (
            <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs py-1 px-2 rounded whitespace-nowrap">
              {shareMessage}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default EcourseCourseDetail;